//dependencies
import { useQueryClient } from "@tanstack/react-query";
import heic2any from "heic2any";
import React, { useRef } from "react";

//components
import { Avatar, toast } from "@performyard/py-components";

import Button from "../../../../../../../components/Button";
import Container from "../../../../../../../components/Container";
import { Divider } from "../../../../../../../components/Divider";
import Spacer from "../../../../../../../components/Spacer";
import Typography from "../../../../../../../components/Text";

//hooks
import {
	avatarBlobUrlQueryKey,
	useDeleteEmployeeAvatar,
	useInternalApiUser,
	useRbacEmployeePermissions,
} from "../../../../../../../api/hooks";

import { scaleImageToCanvas } from "../../../../../../../utils/CropImage";
import avatarURL from "../../../../../../../utils/avatarURL";

//css
import css from "../../AvatarModal.module.scss";

export type AvatarModalContentProps = {
	handleClose: () => void;
	userID: string;
	setFile: (file: File | null) => void;
};

const ViewAvatarModalContents = ({ handleClose, userID, setFile }: AvatarModalContentProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();
	const avatarBlobUrl = queryClient.getQueryData<string>(avatarBlobUrlQueryKey(userID));

	const { data: userInfo, isLoading: isUserLoading } = useInternalApiUser(userID);
	const hasAvatarSet = userInfo?.hasAvatar || false;
	const { mutate: deleteAvatar, isPending: isDeleteAvatarPending } = useDeleteEmployeeAvatar({
		onSuccess: () => {
			toast.success(`Avatar Updated`);
			handleClose();
		},
		meta: {
			errorMessage: "There was a problem updating the employee avatar",
		},
	});

	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(userID, "employee_details");
	const canEmployeeChangeAvatar = rbac?.includes("employee_details:changeAvatar");

	if (isRbacLoading || isUserLoading) return null;

	const scaleAndSetImage = async (file: File) => {
		let processedFile = file;

		// Check if the file is a .heic file
		if (file?.type === "image/heic" || file?.name?.endsWith(".heic")) {
			try {
				// Convert the HEIC file to JPEG using heic2any
				const convertedBlob = await heic2any({
					blob: file,
					toType: "image/jpeg",
				});

				// Create a new File object from the converted Blob
				processedFile = new File([convertedBlob as BlobPart], file.name.replace(/\.heic$/i, ".png"), {
					type: "image/png",
				});
			} catch (error) {
				throw error;
			}
		}

		const image = await scaleImageToCanvas(processedFile);
		setFile(image);
	};

	return (
		<Container column centerVertical center className={`${css.contents} ${css.view}`}>
			<Container column centerVertical center>
				<Avatar id={userID} size={120} name={userInfo?.name || "User"} url={avatarBlobUrl} />
				<Spacer height={2} />
				<Typography size="sub-heading" weight="semi-bold">
					{hasAvatarSet ? "Edit Photo" : "Upload a Custom Photo"}
				</Typography>
			</Container>
			<Spacer height={3} />
			<Divider />
			<Spacer height={2} />
			<div style={{ width: "100%" }}>
				{!hasAvatarSet ? (
					<Container horizontalAlignment="center">
						<input
							key="file-input"
							type="file"
							accept="image/*"
							ref={fileInputRef}
							onChange={async (event) => {
								const file = event.target.files?.[0];
								if (file) {
									scaleAndSetImage(file);
								}
							}}
							style={{ display: "none" }}
						/>
						<Button
							color="green"
							disabled={!canEmployeeChangeAvatar}
							key="upload-photo"
							onClick={() => {
								fileInputRef.current && fileInputRef.current.click();
							}}
						>
							Upload Photo
						</Button>
					</Container>
				) : (
					<Container horizontalAlignment="space-between">
						<Container gap="4px">
							<div>
								<input
									key="file-input"
									type="file"
									accept="image/*"
									ref={fileInputRef}
									onChange={async (event) => {
										//grab image from file input and scale it for cropping
										const file = event.target.files?.[0];
										if (file) {
											scaleAndSetImage(file);
										}
									}}
									style={{ display: "none" }}
								/>
								<Button
									color="green"
									disabled={!canEmployeeChangeAvatar}
									key="upload-photo"
									onClick={() => {
										fileInputRef.current && fileInputRef.current.click();
									}}
								>
									Upload Photo
								</Button>
							</div>
							<Button
								color="green"
								onClick={async () => {
									// fetch the avatar and prep it for cropping
									const fetchUrl = avatarBlobUrl || avatarURL(userID);
									const response = await fetch(fetchUrl);
									const blob = await response.blob();
									const file = new File([blob], "temp-avatar.png", { type: "image/png" });
									scaleAndSetImage(file);
								}}
							>
								Edit
							</Button>
						</Container>
						<Button
							disabled={!canEmployeeChangeAvatar}
							key="reset-to-default"
							onClick={() => deleteAvatar(userID)}
							isRequesting={isDeleteAvatarPending}
							color="red"
						>
							Delete
						</Button>
					</Container>
				)}
			</div>
		</Container>
	);
};

export default ViewAvatarModalContents;
